// /app/frontend/src/components/dashboard/UsageWidget.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Clock, AlertTriangle, TrendingUp, Info } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UsageWidget() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/client/usage`, {
        withCredentials: true
      });
      setUsage(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching usage:', err);
      setError('Unable to load usage data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 animate-pulse">
        <div className="h-4 bg-slate-700 rounded w-1/3 mb-4"></div>
        <div className="h-8 bg-slate-700 rounded w-1/2 mb-2"></div>
        <div className="h-2 bg-slate-700 rounded w-full"></div>
      </div>
    );
  }

  if (error || !usage) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-2 text-slate-400">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">{error || 'No usage data available'}</span>
        </div>
      </div>
    );
  }

  const { subscription_tier, usage: usageData, overage, status, status_color, fair_use_policy } = usage;

  // Status colors
  const statusColors = {
    green: { bg: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/30' },
    amber: { bg: 'bg-amber-500', text: 'text-amber-400', border: 'border-amber-500/30' },
    red: { bg: 'bg-red-500', text: 'text-red-400', border: 'border-red-500/30' }
  };

  const colors = statusColors[status_color] || statusColors.green;

  // Plan display names
  const planNames = {
    demo: 'Demo',
    standard: 'Standard',
    premium: 'Premium'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-slate-800/50 border ${colors.border} rounded-xl p-6`}
      data-testid="usage-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg ${colors.bg}/20 flex items-center justify-center`}>
            <Phone className={`w-4 h-4 ${colors.text}`} />
          </div>
          <div>
            <h3 className="font-semibold text-white">Call Minutes</h3>
            <p className="text-xs text-slate-400">{planNames[subscription_tier]} Plan</p>
          </div>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="w-4 h-4 text-slate-500 hover:text-slate-300 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs bg-slate-800 border-slate-700">
              <p className="text-sm">
                <strong>Fair Use Policy:</strong><br />
                Calls under 5 seconds don't count.<br />
                Overage: ${fair_use_policy?.overage_rate}/minute
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Usage Stats */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1 mb-1">
          <span className="text-3xl font-bold text-white">{usageData.minutes_used}</span>
          <span className="text-slate-400">/ {usageData.minutes_included} min</span>
        </div>
        <Progress 
          value={usageData.usage_percent} 
          className="h-2 bg-slate-700"
          indicatorClassName={colors.bg}
        />
      </div>

      {/* Status Message */}
      {status === 'overage' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-400 font-medium">Overage Alert</span>
          </div>
          <p className="text-xs text-slate-300 mt-1">
            {overage.minutes} minutes over limit @ ${overage.rate_per_minute}/min = <strong>${overage.estimated_cost}</strong>
          </p>
        </div>
      )}

      {status === 'warning' && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-400 font-medium">Approaching Limit</span>
          </div>
          <p className="text-xs text-slate-300 mt-1">
            Only {usageData.minutes_remaining} minutes remaining this period
          </p>
        </div>
      )}

      {/* Details */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-slate-500">Remaining</p>
          <p className={`font-semibold ${colors.text}`}>
            {usageData.minutes_remaining} min
          </p>
        </div>
        <div>
          <p className="text-slate-500">Calls This Month</p>
          <p className="font-semibold text-white">{usageData.billable_calls}</p>
        </div>
      </div>

      {/* Upgrade CTA for demo/standard users hitting limits */}
      {(status === 'warning' || status === 'overage') && subscription_tier !== 'premium' && (
        <div className="mt-4 pt-4 border-t border-slate-700">
          <a 
            href="/pricing" 
            className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1"
            data-testid="upgrade-link"
          >
            Upgrade for more minutes
            <span className="text-xs">→</span>
          </a>
        </div>
      )}
    </motion.div>
  );
}
